import { useEffect, useMemo, useState } from 'react';
import { Button, Field, Header, Message, Modal, Toast, Input } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { bindFormChange } from '../SiteUtil';
import InventoryIconDropdown from './InventoryIconDropdown';
import { InventoryItem, InventoryType, InventoryWarehouse, SetLoading } from '../SiteClasses';
import Validator from '../Validator';
import API from '../API';

interface ModalProps{
	open: boolean,
	onClose: ()=>void,
	warehouse_id?: number,
	defaultType?: InventoryType,
	onCreate?: (item: InventoryItem)=>void,
	onUpdate?: (item: InventoryItem)=>void,
	item_id?: number,
}

var CreateInventoryItemModal = (props: ModalProps)=>{
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [form, setForm] = useState<Partial<InventoryItem>>(null);
	var [isEditing, setIsEditing] = useState<boolean>(false);
	var [itemToEdit, setItemToEdit] = useState<InventoryItem>(null);
	var [types, setTypes] = useState<InventoryType[]>([]);
	var [warehouses, setWarehouses] = useState<InventoryWarehouse[]>([]);

	useEffect(()=>{
		if(!props.open) return;
		getTypes();
		setIsEditing(Boolean(props.item_id));
		if(!props.warehouse_id && !props.item_id){
			API.getWarehouses().then(res=>{
				if(!res.error) setWarehouses(res.data);
			});
		}

		if(props.item_id){
			API.getInventoryItem(props.item_id).then(res=>{
				if(res.error) return setLoadError(res.message);
				setItemToEdit(res.data);
				setForm({
					item_id: res.data.item_id,
					type_id: res.data.type_id,
					warehouse_id: res.data.warehouse_id || props.warehouse_id,
					item_name: res.data.item_name,
					description: res.data.description,
					serial_number: res.data.serial_number,
					model: res.data.model,
					seller_name: res.data.seller_name,
					date_purchase: res.data.date_purchase,
				});
			}).catch(()=>{
				setLoadError('Hubo un error al obtener el equipo (LCL-INV-GET)');
			});
		}else{
			setItemToEdit(null);
			setForm({
				type_id: props.defaultType?.type_id,
				warehouse_id: props.warehouse_id,
				item_name: null,
				description: null,
				serial_number: null,
				model: null,
				seller_name: null,
				date_purchase: null,
			});
		}
	}, [props.open, props.item_id]);

	var getTypes = ()=>{
		API.getInventoryTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			setTypes(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error al obtener los tipos de equipo (LCL-INV-TYPES)');
		});	
	}

	var submit = (setLoading: SetLoading)=>{
		if(!form) return;
		var { valid, prompts } = Validator(form, {
			type_id: [{
				rule: 'empty', label: 'Equipo'
			}],
			...(!isEditing ? {
				warehouse_id: [{
					rule: 'empty', label: 'Almacén'
				}],
			} : {}),
			item_name: [{
				rule: 'length', params: [0, 64], label: 'Nombre'
			}],
			serial_number: [{
				rule: 'length', params: [0, 128], label: 'Número de serie'
			}],
			model: [{
				rule: 'length', params: [0, 128], label: 'Modelo'
			}],
			seller_name: [{
				rule: 'length', params: [0, 64], label: 'Proveedor'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);

		if(isEditing && itemToEdit){
			API.editInventoryItem(itemToEdit.item_id, form).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Equipo actualizado!');
				var updatedItem: InventoryItem = {
					...itemToEdit,
					...form,
				};
				props.onUpdate?.(updatedItem);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado editando el equipo (LCL-INV-2)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.createInventoryItem(form).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Equipo creado!');

				var typeInfo = types.find(t=>t.type_id === form.type_id);
				var createdItem: InventoryItem = {
					item_id: res.data.item_id,
					type_id: form.type_id,
					...form,
					type_icon: typeInfo?.type_icon || props.defaultType?.type_icon,
				};
				props.onCreate?.(createdItem);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado creando el equipo (LCL-INV-1)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	};

	var onFormChange = bindFormChange(form, setForm);

	return (
		<Modal open={props.open} onClose={props.onClose} size='tiny'>
			<Modal.Header>{isEditing ? 'Editar equipo' : 'Agregar equipo'}</Modal.Header>
			<Modal.Content>
				{!form ? (
					loadError ? (
						<Header text='Error' subtext={loadError} />
					) : (
						<Header loading text='Cargando' />
					)
				) : <>
					<Input
						label="Nombre"
						value={form.item_name}
						onChange={onFormChange('item_name')}
					/>				{!props.warehouse_id && !isEditing &&
					<Field label='Almacén'>
						<Dropdown
							placeholder='Seleccionar almacén...'
							fluid
							selection
							value={form.warehouse_id}
							options={warehouses.map(w=>({
								key: w.warehouse_id,
								value: w.warehouse_id,
								text: w.warehouse_name,
							}))}
							onChange={(_, { value })=>setForm(prev=>({ ...prev, warehouse_id: value as number }))}
						/>
					</Field>
				}					{!isEditing &&
						<Field label='Tipo de equipo'>
							<InventoryIconDropdown
								placeholder='Selecciona un tipo de equipo'
								options={types.map(t=>({
									key: t.type_id,
									text: t.type_name,
									value: t.type_id,
									icon: t.type_icon,
								}))}
								value={form.type_id}
								onChange={onFormChange('type_id')}
							/>
						</Field>
					}
					<Field style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
						<Input
							label="Número de serie"
							value={form.serial_number}
							onChange={onFormChange('serial_number')}
						/>
						<Input
							label="Modelo"
							value={form.model}
							onChange={onFormChange('model')}
						/>
					</Field>
					<Field style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
						<Input
							label="Proveedor"
							value={form.seller_name}
							onChange={onFormChange('seller_name')}
						/>
						<Input
							label="Fecha de compra"
							value={form.date_purchase}
							onChange={onFormChange('date_purchase')}
							calendar={{
								date: form.date_purchase,
								mode: 'date',
								format: 'DD/MM/YY'
							}}
						/>
					</Field>
					<Input
						textarea
						textareaRows={4}
						label="Descripción"
						value={form.description}
						onChange={onFormChange('description')}
					/>
					<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={props.onClose} />
				<Button color="black" text='Guardar' onClick={submit} />
			</Modal.Actions>
		</Modal>
	);
};

export default CreateInventoryItemModal;
